import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CartDrawer from "@/components/feature/CartDrawer";
import { useCart } from "@/hooks/useCart";

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const { cartCount } = useCart();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { to: "/", label: t("nav_home") },
    { to: "/shop", label: t("nav_shop") },
    { to: "/custom-gift-box", label: t("nav_custom") },
    { to: "/occasions", label: t("nav_occasions") },
    { to: "/about", label: t("nav_about") },
    { to: "/blog", label: t("nav_blog") },
    { to: "/contact", label: t("nav_contact") },
  ];

  const toggleLang = () => {
    i18n.changeLanguage(i18n.language === "en" ? "fr" : "en");
  };

  const isHome = location.pathname === "/";

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled || !isHome
            ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-amber-100"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gradient-to-br from-amber-500 to-rose-600 shadow-md group-hover:scale-110 transition-transform duration-300">
                <i className="ri-gift-line text-white text-lg"></i>
              </div>
              <div>
                <span
                  className={`font-bold text-lg tracking-tight transition-colors duration-300 ${
                    scrolled || !isHome ? "text-stone-800" : "text-white"
                  }`}
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Creative Hub
                </span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    location.pathname === link.to
                      ? scrolled || !isHome
                        ? "text-amber-700 bg-amber-50"
                        : "text-white bg-white/20"
                      : scrolled || !isHome
                      ? "text-gray-700 hover:text-amber-700 hover:bg-amber-50"
                      : "text-white/90 hover:text-white hover:bg-white/20"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-2">
              {/* Language Toggle */}
              <button
                onClick={toggleLang}
                className={`hidden md:flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  scrolled || !isHome
                    ? "border-amber-200 text-amber-700 hover:bg-amber-50"
                    : "border-white/40 text-white hover:bg-white/20"
                }`}
              >
                <i className="ri-global-line text-sm"></i>
                {i18n.language === "en" ? "FR" : "EN"}
              </button>

              {/* Cart */}
              <button
                onClick={() => setCartOpen(true)}
                className={`relative w-9 h-9 flex items-center justify-center rounded-full transition-all duration-200 cursor-pointer ${
                  scrolled || !isHome
                    ? "text-gray-700 hover:bg-amber-50 hover:text-amber-700"
                    : "text-white hover:bg-white/20"
                }`}
              >
                <i className="ri-shopping-bag-line text-xl"></i>
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-600 text-white text-xs rounded-full flex items-center justify-center font-bold">
                    {cartCount}
                  </span>
                )}
              </button>

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className={`lg:hidden w-9 h-9 flex items-center justify-center rounded-full transition-all duration-200 cursor-pointer ${
                  scrolled || !isHome
                    ? "text-gray-700 hover:bg-rose-50"
                    : "text-white hover:bg-white/20"
                }`}
              >
                <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 bg-white border-t border-amber-100 ${
            menuOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="px-4 py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 cursor-pointer ${
                  location.pathname === link.to
                    ? "text-amber-700 bg-amber-50"
                    : "text-gray-700 hover:text-amber-700 hover:bg-amber-50"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="flex items-center gap-3 pt-3 border-t border-rose-100 mt-2">
              <button
                onClick={toggleLang}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border border-amber-200 text-amber-700 hover:bg-amber-50 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-global-line text-sm"></i>
                {i18n.language === "fr" ? "EN" : "FR"}
              </button>
            </div>
          </div>
        </div>
      </nav>

      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
    </>
  );
}